import React, {
  useCallback, useEffect, useRef, useState,
} from 'react';

import PropTypes from 'prop-types';
import { InputLabel } from '@picsio/ui';

import { ColorPicker } from '../../../../UIComponents';
import Tooltip from '../../../Tooltip/Tooltip';
import productInfoStyles from '../../styles.module.scss';
import styles from '../fields.module.scss';
import ProductFieldRow from './ProductFieldRow';

const ColorField = ({
  data, value, readOnly, isDiff, onChange, validate, savedError, onValidationChange,
}) => {
  const [localValue, setLocalValue] = useState(value ?? '');
  const [error, setError] = useState(savedError);
  const pendingCommitRef = useRef(null);
  const errorRef = useRef(null);
  errorRef.current = error;

  useEffect(() => {
    setError(savedError);
  }, [savedError]);

  const setErrorAndNotify = useCallback((msg) => {
    setError(msg);
    if (onValidationChange) onValidationChange(msg);
  }, [onValidationChange]);

  useEffect(() => {
    if (pendingCommitRef.current !== null) {
      if (value === pendingCommitRef.current) {
        pendingCommitRef.current = null;
      }
      return;
    }
    setLocalValue(value ?? '');
  }, [value]);

  const handleColorChange = useCallback((nextColor) => {
    setLocalValue(nextColor);
    if (errorRef.current && validate?.(nextColor)?.isValid) {
      setErrorAndNotify(null);
    }
  }, [setErrorAndNotify, validate]);

  const handleColorChangeComplete = useCallback((nextColor) => {
    const prevValue = value ?? '';
    setLocalValue(nextColor);

    if (validate) {
      const result = validate(nextColor);
      if (!result.isValid) {
        setErrorAndNotify(result.errorMessage);
        return;
      }
      setErrorAndNotify(null);
    }

    if (nextColor === prevValue) {
      return;
    }
    pendingCommitRef.current = nextColor;
    onChange(nextColor, data, () => {
      setLocalValue(prevValue);
      pendingCommitRef.current = null;
    });
  }, [data, onChange, setErrorAndNotify, validate, value]);

  return (
    <ProductFieldRow title={data.title}>
      <div className={styles.colorField}>
        <InputLabel margin>
          <Tooltip content={isDiff ? 'Unpublished changes' : null} placement="top">
            <span className={isDiff ? productInfoStyles.diffDot : undefined}>{data.title}</span>
          </Tooltip>
        </InputLabel>
        <ColorPicker
          commitOnPickerClose
          initialColor={localValue || '#000000'}
          disabled={readOnly}
          error={error}
          onColorChange={handleColorChange}
          onColorChangeComplete={handleColorChangeComplete}
        />
      </div>
    </ProductFieldRow>
  );
};

ColorField.propTypes = {
  data: PropTypes.shape({ title: PropTypes.string }).isRequired,
  value: PropTypes.string,
  readOnly: PropTypes.bool,
  isDiff: PropTypes.bool,
  validate: PropTypes.func,
  savedError: PropTypes.string,
  onValidationChange: PropTypes.func,
  onChange: PropTypes.func.isRequired,
};

ColorField.defaultProps = {
  value: '',
  readOnly: false,
  isDiff: false,
  validate: null,
  savedError: null,
  onValidationChange: null,
};

export default ColorField;
